import { useChat } from '@ai-sdk/react';
import { DisplayError } from '@cotera/client/app/components';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@cotera/client/v0/components/ai-elements/conversation';
import { Loader } from '@cotera/client/v0/components/ai-elements/loader';
import { Ty } from '@cotera/era';
import {
  ChatStatus,
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
  UIMessage,
} from 'ai';
import { MessageClass } from '@cotera/sdk/core';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { ModelScope, useWatchableValue } from '../actions/framework';
import { cn } from '../lib/utils';
import { CHAT_COLUMN_MAX_WIDTH, CHAT_RAIL_GUTTER } from './chat-layout';
import { ChatModel } from './chat.model';
import { CompactionMessage } from './compaction-message';
import { ChatInput } from './input';
import {
  RenderMessage,
  CrossMessageToolGroup,
  buildMessageRenderPlan,
} from './message';
import { ArtifactInlineDedupeProvider } from './artifact-inline-dedupe-context';
import { useWhimsicalAgentMessage } from './use-whimsical-agent-message';

export interface ChatRef {
  setInputValue: (value: string) => void;
  appendInputValue: (value: string) => void;
  setMessages: (messages: UIMessage[]) => void;
}

interface ChatProps {
  chatId: string;
  agent: string | null;
  systemMessage: string;
  model: string;
  toolIds: string[];
  outputShape: Ty.ExtendedAttributeType;
  inputFooter?: ReactNode;
  customSubmitButton?: ReactNode;
  onSubmit?: (
    message: import('@cotera/client/v0/components/ai-elements/prompt-input').PromptInputMessage
  ) => void;
  onClearChat?: () => void;
  readonly?: boolean;
  /** When true, fetch and restore messages from server on mount */
  restoreFromServer?: boolean;
  /** Called after messages are restored (or restoration fails). Receives message count (0 on error). */
  onMessagesRestored?: (messageCount: number) => void;
  parent: {
    isTargeted: boolean;
    isEqualTo: (other: any) => boolean;
  };
}

export const Chat = forwardRef<ChatRef, ChatProps>((props, ref) => {
  const inputRef = useRef<{
    setValue: (v: string | ((prev: string) => string)) => void;
    setEditorState: (value: { type: 'doc'; content?: unknown[] }) => void;
  }>(null);
  const setMessagesRef = useRef<((messages: UIMessage[]) => void) | null>(null);

  useImperativeHandle(ref, () => ({
    setInputValue: (value: string) => {
      inputRef.current?.setValue(value);
    },
    appendInputValue: (value: string) => {
      inputRef.current?.setValue((prev: string) =>
        prev ? `${prev} ${value}` : value
      );
    },
    setMessages: (messages: UIMessage[]) => {
      setMessagesRef.current?.(messages);
    },
  }));

  return (
    <InnerChatWithRef
      {...props}
      inputRef={inputRef}
      setMessagesRef={setMessagesRef}
    />
  );
});

Chat.displayName = 'Chat';

type UseChatTransportParams = {
  api: string;
  /** Authenticated fetch from the typed client (401 retry + token refresh). */
  chatFetch: typeof fetch;
  orgId: string;
  systemMessage: string;
  toolIds: string[];
  outputShape: Ty.ExtendedAttributeType;
  agent: string | null;
  model: string;
  conversationId: string;
  yoloMode: boolean;
};

const useChatTransport = ({
  api,
  chatFetch,
  orgId,
  systemMessage,
  toolIds,
  outputShape,
  agent,
  model,
  conversationId,
  yoloMode,
}: UseChatTransportParams) => {
  const latestContextRef = useRef({
    systemMessage,
    toolIds,
    outputShape,
    agent,
    model,
    conversationId,
    yoloMode,
  });

  useEffect(() => {
    latestContextRef.current = {
      systemMessage,
      toolIds,
      outputShape,
      agent,
      model,
      conversationId,
      yoloMode,
    };
  }, [
    systemMessage,
    toolIds,
    outputShape,
    agent,
    model,
    conversationId,
    yoloMode,
  ]);

  return useMemo(
    () =>
      new DefaultChatTransport({
        api,
        fetch: chatFetch,
        body: () => {
          const {
            systemMessage: latestSystemMessage,
            toolIds: latestToolIds,
            outputShape: latestOutputShape,
            agent: latestAgent,
            model: latestModel,
            conversationId: latestConversationId,
            yoloMode: latestYoloMode,
          } = latestContextRef.current;

          return {
            context: {
              orgId,
              system: latestSystemMessage,
              toolIds: latestToolIds,
              agent: latestAgent,
              model: latestModel,
              outputShape: latestOutputShape,
              conversationId: latestConversationId,
              yoloMode: latestYoloMode,
            },
          };
        },
      }),
    [api, chatFetch, orgId]
  );
};

const createNoopChatController = (
  id: string,
  messages: UIMessage[] = []
): ReturnType<typeof useChat> => {
  return {
    id,
    messages,
    status: 'ready',
    error: undefined,
    stop: async () => {},
    clearError: () => {},
    setMessages: () => {},
    sendMessage: async () => {},
    reload: async () => {},
    regenerate: async () => {},
    resumeStream: async () => {},
    append: async () => {},
    setInput: () => {},
    setData: () => {},
    setOptions: () => {},
    handleSubmit: async () => {},
    handleInputChange: () => {},
    addToolResult: async () => {},
    addToolOutput: async () => {},
    addToolApprovalResponse: () => {},
    input: '',
    data: undefined,
    isLoading: false,
    setLoading: () => {},
  } as ReturnType<typeof useChat>;
};

const useChatModel = ({
  chatId,
  chatController,
  parent,
  readonly,
  llmModel,
}: {
  chatId: string;
  chatController: ReturnType<typeof useChat>;
  parent: ChatProps['parent'];
  readonly?: boolean;
  llmModel: string;
}): ChatModel => {
  const chatModelRef = useRef<ChatModel>();

  const readonlyParent = useMemo(
    () => ({
      isTargeted: false,
      isEqualTo: (other: unknown) => {
        if (readonly && typeof other === 'object' && other !== null) {
          return (other as { isTargeted?: boolean }).isTargeted === false;
        }
        return parent.isEqualTo(other);
      },
    }),
    [readonly, parent]
  );

  const effectiveParent = readonly ? readonlyParent : parent;
  const effectiveController = useMemo(
    () =>
      readonly
        ? createNoopChatController(chatId, chatController.messages)
        : chatController,
    [readonly, chatController, chatId]
  );

  if (
    !chatModelRef.current ||
    chatModelRef.current.id !== chatId ||
    !chatModelRef.current.parent.isEqualTo(effectiveParent)
  ) {
    chatModelRef.current = new ChatModel(
      chatId,
      effectiveController,
      llmModel,
      effectiveParent
    );
  } else {
    chatModelRef.current.setChatController(effectiveController);
  }

  return chatModelRef.current;
};

const InnerChatWithRef: React.FC<
  ChatProps & {
    inputRef: React.RefObject<{
      setValue: (v: string | ((prev: string) => string)) => void;
      setEditorState: (value: { type: 'doc'; content?: unknown[] }) => void;
    }>;
    setMessagesRef: React.MutableRefObject<
      ((messages: UIMessage[]) => void) | null
    >;
  }
> = (props) => {
  return <InnerChat {...props} />;
};

const InnerChat: React.FC<
  ChatProps & {
    inputRef: React.RefObject<{
      setValue: (v: string | ((prev: string) => string)) => void;
      setEditorState: (value: { type: 'doc'; content?: unknown[] }) => void;
    }>;
    setMessagesRef: React.MutableRefObject<
      ((messages: UIMessage[]) => void) | null
    >;
  }
> = (props) => {
  const {
    model: llmModelName,
    toolIds,
    systemMessage,
    outputShape,
    inputFooter,
    customSubmitButton,
    onSubmit,
    inputRef,
    setMessagesRef,
    readonly = false,
    restoreFromServer = false,
    onMessagesRestored,
  } = props;
  const org = useWhoami((state) => state.org);
  const userId = useWhoami((state) => state.whoami.id);
  const client = useTenantedClient();

  const chatModel = useChatModel({
    chatId: props.chatId,
    chatController: undefined as any, // Will be set below via setChatController
    parent: props.parent,
    readonly,
    llmModel: llmModelName,
  });

  // Use watchable to get reactive yolo mode value
  const yoloMode = useWatchableValue(chatModel.yoloModeWatchable);

  const transport = useChatTransport({
    api: client.playground.chatUrl,
    chatFetch: client.playground.chatFetch,
    orgId: org.id,
    systemMessage,
    toolIds,
    outputShape,
    agent: props.agent,
    model: llmModelName,
    conversationId: props.chatId,
    yoloMode,
  });

  const sendAutomaticallyWhen = useCallback(
    (
      params: Parameters<typeof lastAssistantMessageIsCompleteWithToolCalls>[0]
    ) => lastAssistantMessageIsCompleteWithToolCalls(params),
    []
  );

  const chatController = useChat({
    //to help prevent a maimum update depth exceeded bug - https://github.com/vercel/ai/issues/1610, https://github.com/vercel/ai/issues/5428
    experimental_throttle: 50, // Throttle updates to 50ms
    transport,
    sendAutomaticallyWhen,
  });

  chatModel.setChatController(chatController);

  // Expose setMessages through the ref for external use (e.g., chat history action)
  setMessagesRef.current = chatController.setMessages;

  // Restore messages from server when requested (e.g., after saving a new agent)
  // Use a ref to avoid stale closure issues with chatController.setMessages
  const hasInitiatedRestoreRef = useRef(false);
  const setMessagesLatestRef = useRef(chatController.setMessages);
  setMessagesLatestRef.current = chatController.setMessages;

  if (restoreFromServer && !hasInitiatedRestoreRef.current) {
    hasInitiatedRestoreRef.current = true;
    setTimeout(async () => {
      const result = await client.resource.chat.listMessages({
        conversationId: props.chatId,
        userId,
      });
      if (result.isOk()) {
        const messages = result.value.messages.map(
          (x) => x.message as UIMessage
        );
        setMessagesLatestRef.current(messages);
        onMessagesRestored?.(messages.length);
      } else {
        onMessagesRestored?.(0);
      }
    }, 100);
  }

  const messages = chatController.messages;
  const status = chatController.status;
  const error = chatController.error;

  // Convert UIMessage[] to MessageClass[] for type-safe manipulation
  // Detect compaction messages by their ID prefix (compaction messages have IDs starting with 'compaction')
  const messageInstances = useMemo(
    () =>
      messages.map((msg) => {
        const messageType =
          msg.id?.startsWith('compaction') === true ? 'compaction' : 'chat';
        return MessageClass.fromUIMessage(props.chatId, msg, messageType);
      }),
    [messages, props.chatId]
  );

  const scopedModels = useMemo(() => [chatModel], [chatModel]);

  return (
    <ModelScope models={scopedModels}>
      <div className="h-full flex flex-col min-h-0">
        <Conversation>
          <ConversationContent
            scrollClassName={CHAT_RAIL_GUTTER}
            className={CHAT_COLUMN_MAX_WIDTH}
          >
            <ArtifactInlineDedupeProvider messages={messageInstances}>
              {buildMessageRenderPlan(messageInstances).map((entry) => {
                // A run of consecutive single-tool messages (e.g. sequential
                // edit-agent steps) collapses into one grouped row.
                if (entry.kind === 'toolRun') {
                  return (
                    <div key={`toolrun-${entry.messages[0]!.id}`}>
                      <CrossMessageToolGroup
                        messages={entry.messages}
                        chatModel={chatModel}
                        readonly={readonly}
                      />
                    </div>
                  );
                }

                const message = entry.message;
                // Use type guard to check if this is a compaction message
                if (message.isCompactionMessage()) {
                  return (
                    <div key={message.id}>
                      <CompactionMessage message={message} />
                    </div>
                  );
                }

                return (
                  <div key={message.id}>
                    <RenderMessage
                      chatModel={chatModel}
                      message={message}
                      readonly={readonly}
                    />
                  </div>
                );
              })}
              {(status === 'submitted' || status === 'streaming') && (
                <div className="flex items-center gap-3">
                  <Loader
                    className={cn(
                      'transition-all duration-300 ease-in-out',
                      status === 'streaming' && 'mx-4'
                    )}
                  />
                  <WhimsicalAgentMessage status={status} messages={messages} />
                </div>
              )}
            </ArtifactInlineDedupeProvider>
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>
        <div className={CHAT_RAIL_GUTTER}>
          {error && (
            <div className={cn(CHAT_COLUMN_MAX_WIDTH, 'mx-auto px-4 pb-4')}>
              <DisplayError error={error} />
            </div>
          )}
          {!readonly && (
            <div className={cn(CHAT_COLUMN_MAX_WIDTH, 'mx-auto w-full px-4')}>
              <ChatInput
                ref={inputRef}
                chatModel={chatModel}
                inputFooter={inputFooter}
                customSubmitButton={customSubmitButton}
                onSubmit={onSubmit}
              />
            </div>
          )}
        </div>
      </div>
    </ModelScope>
  );
};

const WhimsicalAgentMessage: React.FC<{
  status: ChatStatus;
  messages: UIMessage[];
}> = ({ status }) => {
  const whimsicalMessage = useWhimsicalAgentMessage({
    status,
  });

  return (
    <span
      aria-live="polite"
      className="text-sm text-muted-foreground animate-pulse"
    >
      {whimsicalMessage}
    </span>
  );
};
